'use client';

import { Dialog } from '@/shared/components/ui/dialog';
import * as React from 'react';
import { MutationKey } from '@tanstack/react-query';
import { useCreateInvite } from '../api/create-invite';
import { LoadingButton } from '@/shared/components/ui/buttons/loading-button';
import { MembershipRole } from '@/types/api/membership';
import { ShareButton } from '@/shared/components/ui/buttons/share-button';
import { CopyButton } from '@/shared/components/ui/buttons/copy-button';
import QRCode from 'react-qr-code';
import { Button, ButtonProps } from '@/shared/components/ui/button';
import { QrCodeIcon, User } from 'lucide-react';

type InviteMembersProps = {
  roles: MembershipRole[];
  orgId: string;
  /** Lets others follow the invite's creation, e.g. onboarding marking its task done */
  mutationKey?: MutationKey;
} & ButtonProps;

// Same tiles as the webshop's share dialog; rows on phones, where three tiles don't fit side by side
const TILE_CLASS =
  'flex justify-start items-center sm:flex-col sm:items-start h-auto py-4 gap-4 [&_[data-slot=icon]]:!size-6 [&>div]:size-6';

export function InviteMembers({ roles, orgId, mutationKey, children, ...rest }: InviteMembersProps) {
  const [open, setOpen] = React.useState(false);
  const [showQR, setShowQr] = React.useState(false);
  const mutation = useCreateInvite({
    mutationConfig: { mutationKey, onSuccess: () => setOpen(true) },
  });

  const title = roles.includes(MembershipRole.Leader) ? 'Bjud in gruppledare' : 'Bjud in deltagare';

  const link = mutation.data?.link ?? '';

  return (
    <Dialog
      title={title}
      trigger={
        <LoadingButton
          {...rest}
          onClick={() => mutation.mutate({ data: { roles }, orgId })}
          isLoading={mutation.isPending}
        >
          {children ?? <DefaultTriggerContent title={title} />}
        </LoadingButton>
      }
      description={title}
      onOpenChange={setOpen}
      open={open}
    >
      {!showQR ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <ShareButton data={{ url: link }} variant={'outline'} className={TILE_CLASS}>
            Dela länk
          </ShareButton>
          <CopyButton value={link} variant={'outline'} className={TILE_CLASS}>
            Kopiera länk
          </CopyButton>
          <Button variant={'outline'} onClick={() => setShowQr(true)} className={TILE_CLASS}>
            <QrCodeIcon data-slot="icon" />
            Visa QR-kod
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-center">Låt medlemmar skanna koden för att gå med i grupp.</p>
          <QRCode value={link} className="mx-auto" />
          <Button variant="ghost" onClick={() => setShowQr(false)} className="w-full">
            Tillbaka
          </Button>
        </div>
      )}
    </Dialog>
  );
}

function DefaultTriggerContent({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-2">
      <User /> {title}
    </div>
  );
}
